import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import 'react-toastify/dist/ReactToastify.css';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Suspense, lazy } from 'react'
import { Provider } from 'react-redux';
import { ErrorFallback } from './views/pages/misc/ErrorFallback';
import { persistor, store } from './store/index.js';
import { injectStore } from './utils/http';
import { PersistGate } from 'redux-persist/integration/react';
import { ThemeProvider } from './components/stores/ThemeProvider';
import { BrowserRouter } from 'react-router-dom';
import './i18n'; // Import i18n configuration
import LoadingButton from './components/loader/LoadingButton';
import 'antd/dist/reset.css'
// import LazyApp from "./App"; // ✅ import bình thường

// Inject store into http client for accessing state in interceptors
injectStore(store)

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: 5 * 60 * 1000, // Data remains fresh for 5 minutes
            cacheTime: 30 * 60 * 1000, // Cache persists for 30 minutes
            refetchOnWindowFocus: false, // Prevent refetch on window focus
            retry: (failureCount, error) => {
                // Don't retry on 403 or 404 errors
                if (error?.response?.status === 403 || error?.response?.status === 404) {
                    return false;
                }
                // Don't retry on network errors (when backend is not running)
                if (!error?.response && error?.request) {
                    return false; // Network error, no retry
                }
                // Limit retry to maximum 1 time for other errors
                return failureCount < 1;
            },
            retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
            suspense: false, // Disable suspense mode
            useErrorBoundary: (error) => {
                return error?.response?.status >= 500;
            },
            onError: async (error) => {
                console.error('error: ', error);
            }
        },
        mutations: {
            retry: false, // Don't retry mutations by default
        }
    }
});

class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props)
        this.state = { hasError: false, error: null }
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error }
    }

    componentDidCatch(error, errorInfo) {
        console.error('Error caught by boundary:', error, errorInfo)
    }

    render() {
        if (this.state.hasError) {
            return (
                <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
                    <div className="text-center">
                        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                            An error occurred
                        </h1>
                        <p className="text-gray-600 dark:text-gray-400 mb-4">
                            Please reload the page or contact support
                        </p>
                        <button
                            onClick={() => window.location.reload()}
                            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                        >
                            Reload page
                        </button>
                    </div>
                </div>
            )
        }

        return this.props.children
    }
}

// const LazyApp = import('./App')
const LazyApp = lazy(() => import('./App'))
const rootElement = document.getElementById('root')
const root = ReactDOM.createRoot(rootElement)
root.render(
    <ErrorBoundary FallbackComponent={ErrorFallback}>
        <Provider store={store}>
            <PersistGate loading={null} persistor={persistor}>
                <Suspense fallback={<div className="flex justify-center items-center h-[calc(100vh-150px)]"><LoadingButton /></div>}>
                    <QueryClientProvider client={queryClient}>
                        <ThemeProvider>
                            <BrowserRouter>
                                <LazyApp />
                            </BrowserRouter>
                        </ThemeProvider>
                    </QueryClientProvider>
                </Suspense>
            </PersistGate>
        </Provider>
    </ErrorBoundary>
)